import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Menu, X, ChevronLeft, ChevronRight, ArrowRight, Shield, Award, Users, Clock, Wrench, MessageCircle, Globe } from "lucide-react";
import { LanguageProvider, useLanguage, LANGUAGES } from "./LanguageContext";
import { ui, getProductText } from "./translations";
import { Helmet } from "react-helmet-async";

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_7583eb2e-df85-4bd0-8d22-2f1c23c08061/artifacts/nyqfkrfi_mektronlogo.png";

// Scroll to Top Component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Ürün verileri
const products = [
  {
    id: "asit-tanklari",
    name: "Asit Tankları",
    shortDesc: "Sülfürik asit, fosforik asit, amonyak ve paslanmaz tank imalatı",
    fullDesc: `Güvenli, Dayanıklı ve Standartlara Uygun Çözümler

Mektron Makine olarak, kimya, petrokimya, gübre ve metal işleme sektörlerinde kullanılan sülfürik asit, hidroklorik asit, fosforik asit, nitrik asit ve benzeri aşındırıcı kimyasallar için özel paslanmaz çelik asit tankları üretiyoruz.

AISI 304, 316L, 316Ti, 904L ve dubleks paslanmaz çelik gibi yüksek korozyon dirençli malzemelerle ürettiğimiz tanklarımız, uzun ömürlü kullanım, sıfır sızıntı riski ve tam güvenlik sunar. 500 litreden 100 ton kapasiteye kadar, dikey veya yatay, izoleli, karıştırıcılı, ısıtma/soğutma ceketli ve özel aksesuarlı (nozul, menhol, seviye sensörü vb.) tasarımlar yapıyoruz.`,
    longDesc: `Neden Mektron Makine?

• Tamamen müşteri ihtiyacına özel mühendislik çözümü
• Uluslararası standartlara (EN 13445, PED, ASME uyumlu) uygun tasarım ve imalat
• Deneyimli kaynak ekibi ve onaylı kaynak prosedürleri
• %100 kalite kontrol ve tahribatsız muayene (PT, RT, UT)
• CE belgesi ve malzeme sertifikaları ile teslimat
• Hızlı üretim + satış sonrası teknik destek

Kimya tesislerinizde asit depolama ve proses tankı ihtiyacınız varsa, Mektron Makine farkını görün. Güvenilirlik ve kaliteyi bir arada sunuyoruz.

Asit Tankı İmalatında Kaynak Prosedürleri ve Kalite Güvencesi

Asit tankları, yüksek korozyon riski taşıyan agresif kimyasalları içerdiği için imalat sürecinde özellikle kaynak kalitesi kritik önem taşır. Mektron Makine'de asit tankı üretimi, uluslararası standartlara tam uyumlu, onaylı prosedürler ile yürütülür.

Kullanılan Malzemeler:
• AISI 304 / 304L
• AISI 316 / 316L / 316Ti
• AISI 904L ve Süper Östenitik / Dubleks kaliteler (yüksek klorür ve asit direnci için)

Kaynak Yöntemleri:
• TIG (GTAW) → Özellikle kök pasoları ve kritik dikişlerde tercih edilir. Temiz, oksidasyon içermeyen ve yüksek kaliteli dikiş sağlar.
• MIG/MAG (GMAW) → Dolgu ve kapak pasolarında kullanılır, verimli ve güçlü birleşme sağlar.
• Gerektiğinde SMAW (örtülü elektrot) yöntemi de uygulanır.

Tüm kaynak işlemleri, onaylı WPS (Kaynak Prosedür Şartnamesi) ve PQR (Prosedür Nitelik Kaydı) dokümanlarına göre gerçekleştirilir. Kaynakçılarımız EN ISO 9606 standardına göre yeterlilik belgelerine sahiptir.

Kaynak Sonrası İşlemler ve Kontroller:
• Pas renk giderme ve pasivasyon (asit tanklarında korozyon direncini artırmak için zorunludur)
• Tahribatsız Muayene (NDT):
  - Penetrant Test (PT) – Yüzey kusurlarını tespit eder
  - Radyografik Test (RT) veya Ultrasonik Test (UT) – İç kusurları kontrol eder
• Hidrostatik test ve sızdırmazlık testi
• Malzeme izlenebilirliği (heat number takibi) ve 3.1 / 3.2 sertifikaları

Uygulanan Standartlar:
• EN 13445 (Basınçlı Kaplar – Avrupa Standardı)
• PED 2014/68/EU Direktifi (CE işaretlemesi için)
• ASME Section VIII (gerektiğinde müşteri talebiyle uyumlu)
• EN ISO 3834-2 Kaynak Kalite Gereksinimleri

Bu sıkı prosedürler sayesinde ürettiğimiz asit tankları, uzun yıllar sorunsuz çalışır, korozyon riskini minimuma indirir ve tesisinizdeki güvenlik standartlarını en üst seviyeye taşır.`,
    features: ["Sülfürik Asit Tankı", "Fosforik Asit Tankı", "Hidroklorik Asit Tankı", "Nitrik Asit Tankı", "CE Belgeli Üretim", "EN 13445 / PED / ASME Uyumlu", "TIG/MIG Kaynak", "Tahribatsız Muayene (NDT)"],
    images: [
      "/images/products/asit-tanklari/fosforik-asit-tanki.jpg",
      "/images/products/asit-tanklari/IMG_0548.JPG",
      "/images/products/asit-tanklari/IMG_7677.jpg",
      "/images/products/asit-tanklari/QEQQ8158.JPG"
    ],
    specs: {
      "Malzeme": "AISI 304/316L/316Ti/904L/Dubleks",
      "Kapasite": "500L - 100 Ton",
      "Tasarım": "Dikey / Yatay / Ceketli",
      "Standartlar": "EN 13445 / PED / ASME"
    }
  },
  {
    id: "reaktor-imalati",
    name: "Reaktör İmalatı",
    shortDesc: "Kimya, ilaç ve gıda endüstrisi için özel tasarım reaktörler",
    fullDesc: `Paslanmaz Çelik Reaktör İmalatı – Kimyasal Reaksiyonlar İçin Güvenilir Çözümler

Mektron Makine olarak, kimya, ilaç, petrokimya, gıda ve kozmetik sektörlerinde kullanılan kimyasal reaktör, proses reaktörü, karıştırmalı reaktör ve basınçlı reaktör imalatında uzmanlaşmış bir üretim anlayışıyla hizmet veriyoruz.

Reaktörlerimiz, yüksek sıcaklık, yüksek basınç, vakum ve agresif kimyasal ortamlara dayanıklı AISI 304, 316L, 316Ti, 904L ve dubleks paslanmaz çelik malzemelerden üretilir. 100 litreden 50 m³ kapasiteye kadar, dikey veya yatay tasarımda, tam müşteri ihtiyacına özel mühendislik çözümleri sunuyoruz.

Reaktörlerimizde Sunulan Özellikler:
• Farklı tip karıştırıcı sistemleri (anchor, propeller, turbine, yüksek kesme vb.)
• Isıtma/soğutma ceketi (yarım boru, dimple jacket veya konvansiyonel ceket)
• Kondenser, menhol, nozul, seviye ve sıcaklık sensörü bağlantıları
• Patlama diskleri, emniyet valfleri ve proses gereksinimlerine uygun aksesuarlar
• İç yüzeylerde mekanik parlatma (Ra 0,8 – 0,4 µm) ve elektro-polishing seçenekleri

Uygulama Alanları:
• Kimyasal sentez ve polimerizasyon reaksiyonları
• İlaç hammaddesi üretimi (API)
• Gıda ve içecek prosesleri
• Petrokimya ve gübre üretimi
• Boya, reçine ve kozmetik imalatı

Mektron Makine reaktörleri, uzun ömürlü kullanım, yüksek verimlilik, tam sızdırmazlık ve kolay bakım avantajı sağlar. Her proje özel olarak tasarlanır ve üretim sürecinin her aşaması titizlikle takip edilir.`,
    longDesc: `Neden Mektron Makine Reaktörü?

• Deneyimli proses mühendisliği ve 3D tasarım desteği
• Uluslararası standartlara (PED, EN 13445, ASME uyumlu) tam uygun imalat
• Onaylı kaynak prosedürleri ve tahribatsız muayene
• CE belgesi ve eksiksiz dokümantasyon paketi ile teslim
• Hızlı üretim süresi ve satış sonrası teknik destek

Tesisinizdeki kimyasal reaksiyon süreçlerini daha güvenli ve verimli hale getirmek için Mektron Makine ile çalışın. Özel reaktör ihtiyacınız için hemen teklif alın.

Reaktör İmalatında Kaynak Prosedürleri ve Kalite Güvencesi

Kimyasal reaktörler, yüksek basınç, yüksek sıcaklık ve aşındırıcı ortamlar altında çalıştığı için imalat kalitesi özellikle kritik öneme sahiptir. Mektron Makine'de reaktör üretimi, uluslararası standartlara ve onaylı prosedürlere göre gerçekleştirilir.

Kullanılan Malzemeler:
• AISI 304 / 304L
• AISI 316 / 316L / 316Ti (genel kimyasal direnç için)
• AISI 904L ve Süper Östenitik / Dubleks paslanmaz çelikler (yüksek korozyon direnci gereken agresif ortamlar için)

Kaynak Yöntemleri:
• TIG (GTAW): Kök pasoları ve kritik dikişlerde tercih edilir. Temiz, oksidasyon içermeyen ve yüksek kaliteli kaynak sağlar. Özellikle paslanmaz çelik reaktörlerin iç yüzeylerinde kullanılır.
• MIG/MAG (GMAW): Dolgu ve kapak pasolarında verimli ve güçlü birleşme için uygulanır.
• Gerektiğinde örtülü elektrot (SMAW) yöntemi de kullanılır.

Tüm kaynak işlemleri, onaylı WPS (Kaynak Prosedür Şartnamesi) ve PQR (Prosedür Nitelik Kaydı) dokümanlarına göre yapılır. Kaynak personelimiz EN ISO 9606 standardına göre yeterlilik belgelerine sahiptir.

Kaynak Sonrası İşlemler ve Kontroller:
• Pasivasyon ve pas rengi giderme işlemleri (korozyon direncini maksimum seviyeye çıkarmak için)
• Tahribatsız Muayene (NDT):
  - Penetrant Test (PT) – Yüzey kusurlarının tespiti
  - Radyografik Test (RT) ve/veya Ultrasonik Test (UT) – İç kusurların kontrolü
• Hidrostatik basınç testi ve sızdırmazlık testi
• Malzeme izlenebilirliği (heat number takibi) ve EN 10204 3.1 / 3.2 malzeme sertifikaları

Uygulanan Standartlar:
• EN 13445 (Basınçlı Kaplar – Avrupa Standardı)
• PED 2014/68/EU Direktifi (CE işaretlemesi için)
• ASME Section VIII (müşteri talebi doğrultusunda uyumlu)
• EN ISO 3834-2 Kaynak Kalite Gereksinimleri

Bu sıkı kalite prosedürleri sayesinde ürettiğimiz reaktörler, uzun yıllar sorunsuz çalışır, reaksiyon verimliliğini artırır ve tesis güvenliğini en üst seviyede sağlar. Müşterilerimize sunduğumuz tam dokümantasyon paketi (tasarım hesapları, WPS/PQR, NDT raporları, test protokolleri, malzeme sertifikaları) ile şeffaf ve güvenilir bir süreç yaşarsınız.`,
    features: ["Kimyasal Reaktör", "Proses Reaktörü", "Karıştırmalı Reaktör", "Basınçlı Reaktör", "Ceket Sistemli", "PED/EN 13445/ASME Uyumlu", "TIG/MIG Kaynak", "Tahribatsız Muayene (NDT)"],
    images: [
      "/images/products/reaktor-imalati/IMG_3709.jpg",
      "/images/products/reaktor-imalati/IMG_4677.jpg",
      "/images/products/reaktor-imalati/IMG_4678.jpg",
      "/images/products/reaktor-imalati/kimyasal-reaktor34.918x0-is.jpg"
    ],
    specs: {
      "Malzeme": "AISI 304/316L/316Ti/904L/Dubleks",
      "Kapasite": "100L - 50 m³",
      "Çalışma Basıncı": "Vakum - 25 Bar",
      "Standartlar": "PED / EN 13445 / ASME"
    }
  },
  {
    id: "sahada-tank-silo",
    name: "Sahada Tank ve Silo İmalatı",
    shortDesc: "Yerinde montaj ile büyük ölçekli tank ve silo üretimi",
    fullDesc: `Büyük Kapasiteli Çözümler, Fabrika Sınırlaması Olmadan

Mektron Makine olarak, müşterilerimizin tesislerinde sahada (yerinde) tank ve silo imalatı hizmeti sunuyoruz. Özellikle büyük hacimli tanklar ve silo sistemleri için fabrika imalatı imkansız veya çok maliyetli olduğunda, sahada profesyonel imalat ve montaj çözümleri üretiyoruz.

Paslanmaz çelik, karbon çelik ve özel alaşımlı malzemelerle ürettiğimiz sahada tank ve silolar, kimya, petrokimya, gıda, çimento, enerji ve su arıtma sektörlerinde yaygın olarak kullanılmaktadır.

Sahada İmalat ve Montajda Sunduğumuz Avantajlar:
• Taşıma ve nakliye sorunu ortadan kalkar (büyük çaplı ve yüksek tanklar için ideal)
• Tesis içinde istenilen noktada üretim ve montaj
• Minimum üretim süresi ile hızlı teslim
• Müşteri sahasına özel tasarım ve mühendislik
• Dikey veya yatay tanklar, depolama siloları, proses tankları ve bunker tipi silolar
• Isı yalıtımlı, ceketli, karıştırıcılı ve özel aksesuarlı seçenekler

Uygulama Alanları:
• Kimyasal madde depolama tankları (asit, baz, solvent vb.)
• Gıda hammaddesi siloları (un, şeker, yağ, süt tozu)
• Çimento, kireç, fly ash ve toz madde siloları
• Su ve atık su arıtma tankları
• Petrokimya ve enerji santrali yakıt/depolama tankları

Mektron Makine'nin sahada imalat ekibi, deneyimli kaynakçılar, kaynak makine parkı ve gerekli tüm ekipmanlarla donatılmıştır. Proje başlangıcından devreye alma aşamasına kadar tüm süreci kendi kontrolümüzde yönetiyoruz.`,
    longDesc: `Neden Mektron Makine Sahada İmalatı?

• Güvenli ve kaliteli kaynak işlemleri (sahada bile aynı fabrika standartları)
• Tam sahada kalite kontrol ve tahribatsız muayene
• CE belgesi, malzeme sertifikaları ve eksiksiz dokümantasyon
• Minimum iş kesintisi ile profesyonel montaj
• Satış sonrası teknik destek ve bakım hizmeti

Sahada Tank ve Silo İmalatında Kalite ve Kaynak Prosedürleri

Sahada tank ve silo imalatı, fabrika ortamına göre daha zorlu koşullarda yapıldığı için disiplinli bir süreç yönetimi ve yüksek kaynak kalitesi gerektirir. Mektron Makine, sahada gerçekleştirdiği tüm imalatları fabrika standartlarında aynı kalite seviyesinde tamamlar.

Kullanılan Malzemeler:
• Paslanmaz çelik: AISI 304, 316L, 316Ti, 904L
• Karbon çelik: S235JR, P265GH, S355J2 vb.
• Özel alaşımlar (müşteri talebine göre)

Sahada Uygulanan Kaynak Yöntemleri:
• TIG (GTAW): Kritik kök pasoları ve paslanmaz çelik yüzeylerde kullanılır. Temiz ve yüksek kaliteli dikiş sağlar.
• MIG/MAG (GMAW): Hızlı dolgu ve kapak pasoları için tercih edilir. Sahada verimliliği artırır.
• SMAW (Örtülü Elektrot): Özellikle karbon çeliklerde ve zor erişimli bölgelerde kullanılır.

Tüm kaynak işlemleri, onaylı WPS (Kaynak Prosedür Şartnamesi) ve PQR (Prosedür Nitelik Kaydı) dokümanlarına göre gerçekleştirilir. Sahada çalışan kaynakçılarımız EN ISO 9606 yeterlilik belgelerine sahiptir.

Sahada Kalite Kontrol ve Muayene Süreçleri:
• Kaynak öncesi ve sonrası pasivasyon (paslanmaz çelik için)
• Tahribatsız Muayene (NDT):
  - Penetrant Test (PT)
  - Ultrasonik Test (UT)
  - Manyetik Parçacık Testi (MT) – karbon çelik için
• Hidrostatik veya pnömotik sızdırmazlık testleri
• Kaynak dikişlerinin %100 görsel kontrolü
• Malzeme izlenebilirliği ve 3.1 / 3.2 sertifikaları

Uygulanan Standartlar:
• EN 13445 (Basınçlı Kaplar)
• PED 2014/68/EU (CE işaretlemesi)
• EN ISO 3834-2 Kaynak Kalite Gereksinimleri
• API 650 / API 620 (büyük depolama tankları için gerektiğinde)

Sahada bile aynı titizlikle uyguladığımız bu prosedürler sayesinde ürettiğimiz tank ve silolar, uzun yıllar sorunsuz hizmet verir, sızıntı riskini minimuma indirir ve tesisinizin güvenlik standartlarını karşılar.

Müşterilerimize sunduğumuz tam dokümantasyon ile (WPS/PQR, NDT raporları, test protokolleri, kaynakçı sertifikaları) şeffaf ve takip edilebilir bir imalat süreci yaşarsınız.`,
    features: ["Sahada İmalat", "Büyük Kapasiteler", "Depolama Tankları", "Toz Siloları", "Kimyasal Tanklar", "CE Belgeli", "NDT Muayene", "API 650/620 Uyumlu"],
    images: [
      "/images/products/sahada-tank-silo/238a2dfb-cd1f-4995-8774-fa85b9af1c32.jpg",
      "/images/products/sahada-tank-silo/IMG_7669.jpg",
      "/images/products/sahada-tank-silo/IMG_7676.jpg",
      "/images/products/sahada-tank-silo/QEQQ8158.JPG"
    ],
    specs: {
      "Kapasite": "10 m³ - 5.000 m³",
      "Malzeme": "Paslanmaz / Karbon Çelik",
      "Tasarım": "Dikey / Yatay / Bunker",
      "Standartlar": "EN 13445 / PED / API 650"
    }
  },
  {
    id: "mikser",
    name: "Mikser",
    shortDesc: "Endüstriyel karıştırıcı ve mikser sistemleri",
    fullDesc: "Paslanmaz Çelik Mikser İmalatı – Homojen Karışım ve Yüksek Verimlilik İçin Özel Çözümler\n\nMektron Makine olarak, kimya, ilaç, gıda, boya, kozmetik, reçine ve petrokimya sektörlerinde kullanılan endüstriyel mikser, karıştırıcı, karıştırmalı tank ve yüksek kesme mikseri imalatında uzmanlaşmış bir üretim anlayışı ile hizmet veriyoruz.\n\nPaslanmaz çelikten ürettiğimiz mikserlerimiz, 50 litre'den 30 m³ kapasiteye kadar, dikey veya yatay tanklı, farklı viskozite ve proses koşullarına özel olarak tasarlanmaktadır. Her mikser, müşterimizin prosesine göre mühendislik hesapları yapılarak üretilir.\n\nÜrettiğimiz Mikser Tipleri:\n• Anchor tipi mikser (yüksek viskoziteli ürünler için)\n• Propeller ve turbine tipi karıştırıcılar\n• Yüksek kesme (high shear) mikser ve disperser\n• Çift şaftlı ve planet tipi mikserler\n• Manyetik tahrikli (sealless) mikserler\n• Mobil ve sabit karıştırıcı sistemleri\n\nÖzellikler:\n• AISI 304, 316L, 316Ti ve 904L paslanmaz çelik malzeme seçenekleri\n• Mekanik salmastra veya manyetik tahrik seçenekleri (sızıntısız çalışma)\n• Değişken devir kontrollü (inverter) motor sistemleri\n• İç yüzeylerde mekanik parlatma ve elektro-polishing (ilaç ve gıda sektörü için Ra ≤ 0,4 µm)\n• Isıtma/soğutma ceketli tanklı mikser seçenekleri\n• Patlama korumalı (Ex-proof) motor ve ekipman seçenekleri\n\nUygulama Alanları:\n• Kimyasal reaksiyon ve homojenizasyon\n• İlaç hammaddesi (API) ve şurup üretimi\n• Gıda ürünleri (sos, reçel, meyve suyu, süt ürünleri)\n• Boya, vernik, mürekkep ve kaplama maddeleri\n• Kozmetik kremler, losyonlar ve şampuan üretimi\n• Petrokimya ve polimer prosesleri\n\nMektron Makine mikserleri, mükemmel homojen karışım, enerji verimliliği, uzun ömür ve kolay temizlik özellikleri ile proseslerinizi optimize eder.\n\nNeden Mektron Makine Mikseri?\n• Prosesinize özel mühendislik ve 3D tasarım desteği\n• Uluslararası standartlara (PED, CE, GMP uyumlu) imalat\n• Deneyimli kaynak ve montaj ekibi\n• Tam kalite kontrol ve tahribatsız muayene\n• CE belgesi ve eksiksiz teknik dokümantasyon\n• Hızlı üretim ve satış sonrası teknik destek\n\nÜretim tesisinizde karıştırma proseslerini daha verimli ve güvenilir hale getirmek için Mektron Makine ile çalışın.",
    features: ["Değişken Hız Kontrolü", "Farklı Pervane Tipleri", "Sessiz Çalışma", "Kolay Montaj", "Uzun Ömürlü Motor", "Özel Tasarım"],
    images: [
      "/images/products/mikser/IMG_3706.jpg",
      "/images/products/mikser/konik-blender.jpg",
      "/images/products/mikser/NUBE0679.JPG",
      "/images/products/mikser/v-tipi-blender.jpg"
    ],
    specs: {
      "Motor Gücü": "0.5 kW - 75 kW",
      "Devir Aralığı": "10 - 3000 RPM",
      "Şaft Uzunluğu": "Özel Ölçü",
      "Pervane Tipi": "Propeller / Türbin / Çapa"
    }
  },
  {
    id: "evaporasyon-sistemleri",
    name: "Evaporasyon Sistemleri",
    shortDesc: "Endüstriyel buharlaştırma ve konsantrasyon sistemleri",
    fullDesc: "Başlıca Evaporasyon Sistemi Türleri ve Görselleri\n\n1. Çok Etkili Evaporatör (Multi-Effect Evaporator - MEE)\nEn yaygın ve enerji verimli sistemdir. Buhar, birden fazla etkide (stage) tekrar kullanılarak enerji tasarrufu sağlar.\nAvantajları:\n• Yüksek kapasite\n• Düşük buhar tüketimi (1 kg buhar ile 3-6 kg su buharlaştırılabilir)\n\n2. Düşen Film Evaporatör (Falling Film Evaporator)\nSıvı, boruların iç yüzeyinden ince bir film halinde akar. Özellikle gıda (meyve suyu, süt) ve hassas ürünlerde tercih edilir.\nAvantajları:\n• Kısa temas süresi (ürün yanmaz)\n• Yüksek ısı transferi\n• Düşük sıcaklıkta çalışma\n\n3. Zorlamalı Sirkülasyon Evaporatör (Forced Circulation Evaporator)\nYüksek viskoziteli, kristalleşme eğilimi olan veya kirletici sıvılar için idealdir. Pompa ile sıvı zorla dolaştırılır.\nAvantajları:\n• Tıkanma riski çok düşük\n• Kristal üretimi için uygundur\n\n4. İnce Film / Sıyrılmış Film Evaporatör (Thin Film / Wiped Film Evaporator)\nDönen bir mil üzerindeki bıçaklar sıvıyı ince bir film haline getirir. Çok viskoz veya hassas ürünlerde kullanılır.\nAvantajları:\n• Çok kısa residence time (saniyeler)\n• Yüksek vakumda düşük sıcaklıkta çalışma\n\n5. Vakumlu / Isı Pompası Evaporatör (Vacuum / Heat Pump Evaporator)\nAtık su arıtma ve düşük enerji tüketimi gereken yerlerde tercih edilir.\n\nMektron Makine Olarak Sunduğumuz Evaporasyon Sistemleri:\n• Çok Etkili Evaporatör (2-6 etki)\n• Düşen Film + Zorlamalı Sirkülasyon kombinasyonları\n• Atık Su Konsantrasyon Sistemleri\n• Kristalizatörlü evaporasyon üniteleri\n• Paslanmaz çelik (316L, 904L) ve CTP (FRP) malzeme seçenekleri\n• Tam otomasyon ve SCADA sistemleri\n\nNeden Mektron Makine?\n• Prosesinize özel termal tasarım\n• PED / CE standartlarında imalat\n• Enerji verimliliği odaklı çözümler\n• Sahada montaj ve devreye alma hizmeti",
    features: ["Çok Etkili Evaporatör", "Düşen Film Evaporatör", "Zorlamalı Sirkülasyon", "İnce Film Evaporatör", "Vakumlu Evaporatör", "PLC + SCADA"],
    images: [
      "/images/products/evaporasyon/cok-etkili-evaporator-1.jpeg",
      "/images/products/evaporasyon/cok-etkili-evaporator-2.jpeg",
      "/images/products/evaporasyon/dusen-film-evaporator.jpeg",
      "/images/products/evaporasyon/zorlamali-sirkulasyon.png",
      "/images/products/evaporasyon/ince-film-evaporator.png",
      "/images/products/evaporasyon/vakumlu-evaporator.jpeg",
      "/images/products/evaporasyon/mektron-evaporator-1.jpeg",
      "/images/products/evaporasyon/mektron-evaporator-2.png",
      "/images/products/evaporasyon/mektron-evaporator-3.jpeg",
      "/images/products/evaporasyon/mektron-evaporator-4.jpeg"
    ],
    specs: {
      "Kapasite": "100 - 50.000 L/saat",
      "Çalışma Basıncı": "Vakum - 3 Bar",
      "Isıtma": "Buhar / Termik Yağ",
      "Otomasyon": "PLC + SCADA"
    }
  },
  {
    id: "yikama-kulesi",
    name: "Yıkama Kulesi",
    shortDesc: "Gaz yıkama ve arıtma kuleleri (Scrubber)",
    fullDesc: "Gaz Yıkama Kulesi (Scrubber) İmalatı – Paslanmaz Çelik ve CTP Malzeme Seçenekleriyle Çevre Dostu Arıtma Çözümleri\n\nMektron Makine olarak, kimya, petrokimya, ilaç, gıda, metal işleme ve atık yönetimi sektörlerinde çıkan zararlı gazlar, asit buharları, toz partiküller ve kokuları etkin şekilde temizlemek için gaz yıkama kulesi (scrubber) imalatı yapıyoruz.\n\nMüşterilerimizin proses koşullarına, bütçesine ve korozyon gereksinimlerine göre paslanmaz çelik (AISI 304, 316L, 904L) veya CTP (Cam Elyaf Takviyeli Plastik / FRP) malzemelerinden yüksek kaliteli scrubber sistemleri üretiyoruz. Kapasite aralığımız 1.000 m³/saat'ten 500.000 m³/saat'e kadar olup, her proje özel olarak tasarlanmaktadır.\n\nÜrettiğimiz Gaz Yıkama Kulesi Tipleri:\n• Paket dolgulu (packed bed) scrubber\n• Sprey kuleli (spray tower) sistemler\n• Asit / baz yıkama kuleleri\n• Toz tutma ve koku giderme sistemleri\n• Kombine (çok kademeli) scrubber çözümleri\n\nMalzeme Seçenekleri ve Avantajları:\n\nPaslanmaz Çelik Scrubber\n• AISI 316L, 904L ve dubleks kaliteler ile yüksek korozyon direnci\n• Yüksek sıcaklık ve basınç dayanımı\n• Uzun ömür ve mekanik mukavemet\n• İlaç ve gıda gibi hijyenik uygulamalar için ideal\n\nCTP (FRP) Scrubber\n• Poliester, vinilester veya izoftalik reçine ile üretilir\n• Aşındırıcı asit ve baz gazlarına karşı mükemmel kimyasal direnç\n• Daha hafif yapı (nakliye ve montaj kolaylığı)\n• Sahada imalat ve büyük çaplı kuleler için maliyet avantajı\n• Korozyona karşı paslanmaz çeliğe alternatif olarak uzun yıllar sorunsuz kullanım\n\nOrtak Özellikler:\n• Raschig, pall halka veya yapılandırılmış dolgu malzemeleri\n• Verimli püskürtme nozul sistemi ve demister (sis tutucu)\n• pH kontrolü, sirkülasyon pompası ve otomatik kimyasal dozaj\n• Isı yalıtımı ve Ex-proof (patlama korumalı) seçenekler\n\nUygulama Alanları:\n• HCl, SO₂, NH₃, H₂S gibi asit ve baz gazlarının arıtılması\n• Boya, solvent ve reçine buharları\n• Gıda ve ilaç tesislerinde koku giderme\n• Metal işleme ve galvanizdeki asit buharları\n• Endüstriyel baca gazı arıtma ve çevre mevzuatına uyum\n\nMektron Makine scrubber sistemleri, %95-99+ arıtma verimliliği, düşük işletme maliyeti ve uzun ömür sunar. Paslanmaz çelik mi yoksa CTP mi daha uygun olduğuna karar vermenize yardımcı oluyoruz.\n\nNeden Mektron Makine?\n• Prosesinize özel mühendislik tasarımı ve simülasyon\n• Hem paslanmaz çelik hem CTP malzeme konusunda uzman üretim\n• Uluslararası standartlara (PED, CE, EN 13445) uyumlu imalat\n• Tam kalite kontrol, tahribatsız muayene ve dokümantasyon\n• Hızlı üretim + satış sonrası teknik destek\n\nGaz Yıkama Kulesi İmalatında Malzeme Seçenekleri, Üretim Prosedürleri ve Kalite Güvencesi\n\nGaz yıkama kuleleri sürekli agresif kimyasal gazlarla temas halinde çalıştığı için malzeme seçimi ve üretim kalitesi çok önemlidir. Mektron Makine, projenize en uygun çözümü sunmak için paslanmaz çelik ve CTP (Cam Elyaf Takviyeli Plastik) olmak üzere iki ana malzeme seçeneği sunar.\n\nMalzeme Seçenekleri:\n• Paslanmaz Çelik: AISI 304 / 316L / 316Ti / 904L – Yüksek mekanik dayanım ve orta-yüksek sıcaklık uygulamaları için tercih edilir.\n• CTP (FRP): Cam elyaf takviyeli poliester, vinilester veya izoftalik reçine – Üstün kimyasal direnç, hafiflik ve maliyet avantajı sağlar. Özellikle yüksek konsantrasyonlu asit/baz gazlarında uzun ömürlüdür.\n\nÜretim ve Kaynak / Laminasyon Prosedürleri:\n\nPaslanmaz Çelik Kuleler İçin:\n• TIG (GTAW) ve MIG/MAG (GMAW) kaynak yöntemleri\n• Onaylı WPS / PQR dokümanlarına göre kaynak\n• Kaynak sonrası pasivasyon ve pas rengi giderme\n• Tahribatsız muayene (PT, UT, RT)\n\nCTP (FRP) Kuleler İçin:\n• El yatırması (hand lay-up) ve filament sarım (filament winding) teknikleri\n• Katman katman cam elyaf ve reçine uygulaması ile özel laminasyon\n• Reçine seçimi proses gazının kimyasal yapısına göre yapılır (izoftalik, vinilester vb.)\n• UV korumalı dış katman ve aşınma dirençli iç yüzey seçenekleri\n\nOrtak Kalite Kontrol Süreçleri:\n• Tam sızdırmazlık ve hidrostatik / pnömatik testler\n• Dolgu destekleri, nozul ve demister montajında hassas kontrol\n• Malzeme izlenebilirliği ve sertifikaları\n• Tahribatsız muayene ve performans testleri\n\nUygulanan Standartlar:\n• EN 13445 (Basınçlı Kaplar)\n• PED 2014/68/EU (CE işaretlemesi)\n• EN ISO 3834-2 (Kaynak Kalite Gereksinimleri)\n• CTP için ilgili GRP/FRP üretim standartları\n\nBu prosedürler sayesinde ürettiğimiz gaz yıkama kuleleri (ister paslanmaz çelik ister CTP), yüksek arıtma verimliliğiyle uzun yıllar sorunsuz çalışır, korozyon riskini minimuma indirir ve çevre mevzuatına tam uyum sağlar. Müşterilerimize sunduğumuz tam dokümantasyon paketi ile şeffaf bir süreç yaşarsınız.",
    features: ["Asit/Baz Yıkama", "Toz Tutma Sistemleri", "Koku Giderme", "Paket Dolgulu", "Sprey Sistemli", "Çevre Uyumlu"],
    images: [
      "/images/products/yikama-kulesi/IMG_7573.jpg",
      "/images/products/yikama-kulesi/IMG_7575.jpg",
      "/images/products/yikama-kulesi/IMG_7583.jpg",
      "/images/products/yikama-kulesi/IMG_7573.jpg"
    ],
    specs: {
      "Kapasite": "1.000 - 500.000 m³/saat",
      "Malzeme": "PP / HDPE / Paslanmaz",
      "Dolgu": "Raschig / Pall Ring / Tellerette",
      "Verimlilik": "%95-99 Gaz Tutma"
    }
  },
  {
    id: "esanjor",
    name: "Eşanjör",
    shortDesc: "Isı değiştiriciler ve eşanjör sistemleri",
    fullDesc: "Borulu Eşanjör (Shell & Tube Heat Exchanger) İmalatı – Yüksek Verimli Isı Transfer Çözümleri\n\nMektron Makine olarak, kimya, petrokimya, ilaç, gıda, enerji ve su arıtma sektörleri için borulu tip eşanjör (shell & tube heat exchanger) tasarlıyor ve üretiyoruz.\n\nProsesinizdeki ısıtma, soğutma, kondensasyon veya buharlaştırma ihtiyaçlarına özel olarak üretilen eşanjörlerimiz, yüksek ısı transfer verimliliği, dayanıklılık ve uzun ömür sunar. 0,5 m²'den 500 m²'ye kadar ısı transfer yüzeyine sahip, tek geçişli, çok geçişli, sabit boru plakalı veya yüzer başlıklı (floating head) tasarımlar yapıyoruz.\n\nÜrettiğimiz Borulu Eşanjör Tipleri:\n• Sabit boru plakalı (Fixed Tube Sheet)\n• Yüzer başlıklı (Floating Head)\n• U-tüp (U-Tube) eşanjör\n• Kondenser ve reboiler tipi\n• Çok borulu ve multi-pass sistemler\n• Yüksek basınç ve yüksek sıcaklık uygulamaları için özel tasarımlar\n\nMalzeme Seçenekleri:\n• Paslanmaz Çelik: AISI 304, 316L, 316Ti, 904L\n• Dubleks ve Süper Dubleks paslanmaz çelik\n• Karbon çelik + paslanmaz boru kombinasyonu\n• Titanyum ve özel alaşımlar (talep üzerine)\n\nUygulama Alanları:\n• Kimyasal reaksiyonlarda ısıtma/soğutma\n• Sülfürik asit, hidroklorik asit ve agresif kimyasalların soğutulması\n• Buhar kondensasyonu ve buharlaştırma\n• Gıda ürünlerinde pastörizasyon ve soğutma\n• Enerji santralleri ve kojenerasyon sistemleri\n• Petrokimya ve rafineri prosesleri\n\nMektron Makine borulu eşanjörleri, yüksek ısı transfer katsayısı, minimum basınç kaybı, kolay bakım ve uzun servis ömrü avantajları ile proses verimliliğinizi artırır.",
    features: ["Plakalı Eşanjör", "Borulu Eşanjör", "Spiral Eşanjör", "Yüksek Verimlilik", "Kompakt Tasarım", "Kolay Bakım"],
    images: [
      "/images/products/esanjor/630017b6-f85a-4859-b200-f39dcaef33b5.jpg",
      "/images/products/esanjor/IMG_5014.jpg",
      "/images/products/esanjor/IMG_5019.jpg",
      "/images/products/esanjor/IMG_5383.jpg"
    ],
    specs: {
      "Tip": "Plakalı / Borulu / Spiral",
      "Kapasite": "10 - 10.000 kW",
      "Malzeme": "AISI 304 / 316L / Titanyum",
      "Basınç": "0 - 25 Bar"
    }
  },
  {
    id: "tesisat-hizmetleri",
    name: "Tesisat Hizmetleri",
    shortDesc: "Buhar, asit ve gaz hatları tesisat projeleri",
    fullDesc: "Mektron Makine, ürettiği paslanmaz çelik ekipmanların yanı sıra kimya, petrokimya, ilaç ve gıda tesisleri için özel proses boru tesisatı ve montaj hizmetleri sunmaktadır.\n\nDeneyimli ekibimizle asit, buhar, yüksek basınç ve gaz hatları başta olmak üzere komple tesisat çözümleri üretiyor, montajını yapıyor ve devreye alıyoruz.\n\nProfesyonel Tesisat Hizmetlerimiz\n\n1. Asit Hatları Tesisatı\n• Sülfürik asit, hidroklorik asit, nitrik asit, fosforik asit vb. agresif kimyasallar için özel boru hatları\n• Malzeme: AISI 316L, 904L, Dubleks ve Süper Dubleks paslanmaz çelik\n• PTFE kaplı boru ve fitting seçenekleri\n• Yüksek korozyon direnci ve sızdırmazlık garantisi\n• Pasivasyon ve tahribatsız muayene işlemleri\n\n2. Buhar Hatları Tesisatı\n• Doymuş ve kızgın buhar hatlarının profesyonel tasarımı ve montajı\n• Karbon çelik ve paslanmaz çelik boru seçenekleri\n• Genleşme derzleri, kondens tahliye sistemleri ve yalıtım uygulamaları\n• Yüksek sıcaklık ve basınca dayanıklı kaynak ve montaj\n• Buhar dağıtım manifoldları ve trapi (buhar tuzakları) montajı\n\n3. Yüksek Basınç Hatları Tesisatı\n• 40 bar ve üzeri çalışma basıncına sahip proses hatları\n• ASME ve PED standartlarına uygun tasarım ve imalat\n• Kalın cidarlı borular, yüksek basınç flanşları ve fittingler\n• Hidrostatik basınç testi ve sertifikasyon\n• Reaktör besleme ve yüksek basınçlı transfer hatları\n\n4. Gaz Hatları Tesisatı\n• Proses gazları (azot, oksijen, hidrojen, amonyak, SO₂, HCl gazı vb.)\n• Patlama korumalı (Ex-proof) hat tasarımı ve montajı\n• Paslanmaz çelik ve özel alaşımlı boru sistemleri\n• Gaz scrubber bağlantıları ve emniyet sistemleri\n• Sızdırmazlık testleri\n\nDiğer Tesisat Hizmetlerimiz:\n• Genel proses boru tesisatı ve ekipman montajı\n• Sahada tank, reaktör, mikser, eşanjör ve evaporatör montajı\n• Isı ve ses yalıtım uygulamaları\n• Otomasyon, enstrümantasyon ve elektrik hatları\n• Komple tesis devreye alma ve personel eğitimi",
    features: ["Buhar Hatları", "Asit Hatları", "Gaz Hatları", "Proses Boruları", "Vana Grupları", "Komple Tesisat"],
    images: [
      "/images/products/tesisat-hizmetleri/CJAV8498.JPG",
      "/images/products/tesisat-hizmetleri/IMG_1126.JPG",
      "/images/products/tesisat-hizmetleri/KKVB4698.JPG",
      "/images/products/tesisat-hizmetleri/MBGZ5098.JPG"
    ],
    specs: {
      "Hizmet": "Tasarım + İmalat + Montaj",
      "Malzeme": "Karbon / Paslanmaz / PP",
      "Basınç Sınıfı": "PN10 - PN40",
      "Çap Aralığı": "DN15 - DN1000"
    }
  },
  {
    id: "kukurt-ergitme",
    name: "Kükürt Ergitme Tesisleri",
    shortDesc: "Kükürt eritme ve işleme sistemleri",
    fullDesc: "Kükürt Ergitme Tesisi İmalatı – Katı Kükürtü Verimli ve Güvenli Şekilde Sıvı Hale Getirme Çözümleri\n\nMektron Makine olarak, gübre, kimya, petrokimya ve kauçuk sektörlerinde kullanılan kükürt ergitme tesisi, kükürt eritme tankı, sıvı kükürt depolama sistemi ve tam entegre kükürt melting üniteleri tasarlayıp üretiyoruz.\n\nYıllık 500 ton'dan 50.000 ton'a kadar kapasiteli tesisleri anahtar teslim veya ekipman bazında imal ediyoruz. Katı kükürtü (pastil, külçe veya toz) kontrollü sıcaklıkta eriterek, yüksek akışkanlıkta ve safiyette sıvı kükürt elde etmenizi sağlıyoruz.\n\nTesiste Sunduğumuz Ana Ekipmanlar:\n• Buharlı veya termik yağlı ısıtmalı kükürt eritme tankları\n• Agitatörlü (karıştırmalı) eritme ve bekletme tankları\n• Sıvı kükürt depolama tankları (izoleli ve ısıtmalı)\n• Filtrasyon ve safsızlık ayırma sistemleri\n• Sıvı kükürt transfer pompaları ve boru hatları (ısı izlemeli)\n• Gaz yıkama kulesi (scrubber) entegrasyonu (SO₂ ve toz arıtma)\n• Otomatik seviye, sıcaklık ve basınç kontrol sistemleri\n• Ex-proof (patlama korumalı) elektrik ve otomasyon panelleri\n\nSistem Özellikleri:\n• Paslanmaz çelik (AISI 304, 316L) ve karbon çelik + özel kaplama seçenekleri\n• CTP (FRP) malzeme alternatifi (yüksek korozyonlu ortamlarda)\n• 120°C – 150°C çalışma sıcaklığı aralığı\n• Yüksek enerji verimliliği ve minimum ısı kaybı\n• Tam otomatik veya yarı otomatik çalışma seçenekleri\n• Kolay temizlik ve bakım tasarımı\n\nUygulama Alanları:\n• Kompoze gübre (NPK, DAP) üretim tesisleri\n• Sülfürik asit üretim tesisleri\n• Kauçuk vulkanizasyonu ve lastik sanayi\n• Petrol rafinerileri ve petrol katkı maddeleri üretimi\n• Tarım ve pestisit üretimi\n\nMektron Makine kükürt ergitme tesisleri, yüksek ergitme verimliliği, güvenli çalışma, düşük işletme maliyeti ve çevre mevzuatına uyum sağlar. Tesisi anahtar teslim kurarak devreye alma hizmetimiz de mevcuttur.",
    features: ["Ergitme Tankları", "Filtrasyon Sistemleri", "Pompalama Üniteleri", "Depolama Tankları", "Isıtma Sistemleri", "Anahtar Teslim"],
    images: [
      "/images/products/kukurt-ergitme/melting-tank-3d.png",
      "/images/products/kukurt-ergitme/kukurt-tesis-3d.png",
      "/images/products/kukurt-ergitme/sulphur-recovery.webp",
      "/images/products/kukurt-ergitme/sulphur-tank.jpg",
      "/images/products/kukurt-ergitme/kukurt-eritme-tanki.jpg"
    ],
    specs: {
      "Kapasite": "500 - 50.000 Ton/yıl",
      "Ergitme Sıcaklığı": "120 - 150°C",
      "Isıtma": "Buhar / Termik Yağ",
      "Otomasyon": "PLC Kontrollü"
    }
  },
  {
    id: "konveyor-imalati",
    name: "Konveyör İmalatı",
    shortDesc: "Endüstriyel taşıma ve konveyör sistemleri",
    fullDesc: "Mektron Makine olarak, kimya, gıda, ilaç, ambalaj, madencilik ve otomotiv sektörleri için paslanmaz çelik ve karbon çelik konveyör sistemleri tasarlıyor ve üretiyoruz.\n\nÜretim hatlarınızın ihtiyaçlarına göre hijyenik veya ağır sanayi tipi konveyör çözümleri sunuyoruz. 1 metreden 50 metreye kadar değişen uzunluklarda, özel tasarım ve yüksek kalite standartlarında imalat yapıyoruz.\n\nÜrettiğimiz Konveyör Tipleri:\n• Modüler Bant Konveyörler (Plastik modüler bantlı)\n• Paslanmaz Çelik Tel Bant Konveyörler\n• Vidalı (Helezon) Konveyörler – Screw Conveyor\n• Zincirli Konveyörler\n• Roller (Silindir) Konveyörler (Tahrikli ve serbest)\n• Özel Tasarım Konveyör Sistemleri\n\nMalzeme Seçenekleri:\n• Paslanmaz Çelik (AISI 304 – 316L) → Gıda, ilaç ve korozyon riski yüksek ortamlar için\n• Karbon Çelik (S235JR, S355JR vb.) → Ağır sanayi, madencilik ve genel endüstriyel uygulamalar için\n• Yüzey işlemleri: Boya, galvaniz, mekanik parlatma ve pasivasyon\n\nÖzellikler:\n• Değişken hız kontrollü motor ve redüktör sistemleri\n• Kolay montaj ve bakım tasarımı\n• Güvenlik korumaları ve acil stop sistemleri\n• Gıda onaylı bant ve contalar (paslanmaz modellerde)\n• IP65/IP67 elektrik panelleri\n\nUygulama Alanları:\n• Gıda üretim ve paketleme hatları\n• Kimyasal toz ve granül taşıma\n• Ambalaj ve paletleme sistemleri\n• Madencilik ve dökme malzeme taşıma\n• Boya, reçine ve kozmetik üretim hatları\n• Genel fabrika içi malzeme akışı",
    features: ["Modüler Bant", "Tel Bant", "Helezon Konveyör", "Zincirli Konveyör", "Roller Konveyör", "Özel Tasarım"],
    images: [
      "/images/products/konveyor/k1.jpg",
      "/images/products/konveyor/k2.jpg",
      "/images/products/konveyor/k3.jpg"
    ],
    specs: {
      "Tip": "Bant / Helezon / Zincir / Roller",
      "Uzunluk": "1 - 50 metre",
      "Malzeme": "AISI 304-316L / Karbon Çelik",
      "Koruma": "IP65 / IP67"
    }
  },
  {
    id: "elevator-imalati",
    name: "Elevatör İmalatı",
    shortDesc: "Dikey taşıma ve elevatör sistemleri",
    fullDesc: "Mektron Makine olarak, dökme malzeme taşıma ihtiyaçlarınız için kova tipi elevatör (Bucket Elevator) ve Z tipi elevatör sistemleri tasarlıyor ve üretiyoruz.\n\nToz, granül, pellet ve benzeri malzemelerin dikey taşımasında yüksek verimlilik, minimum malzeme kaybı ve uzun ömür sunan elevatörlerimiz, gıda, kimya, madencilik, yem, un ve plastik sektörlerinde yaygın olarak tercih edilmektedir.\n\nÜrettiğimiz Elevatör Tipleri:\n• Kova Tipi Elevatör (Bucket Elevator) – Zincirli veya bantlı sistemler, plastik veya metal kovalarla\n• Z Tipi Elevatör – Özellikle hijyenik ve nazik malzeme taşıma için ideal\n\nMalzeme Seçenekleri:\n• Paslanmaz Çelik (AISI 304 – 316L) → Gıda, ilaç ve korozyon riski yüksek ortamlar\n• Karbon Çelik → Madencilik, yem, gübre ve ağır sanayi uygulamaları\n• Gerektiğinde galvaniz veya özel kaplama\n\nÖzellikler:\n• 5 m'den 40 m'ye kadar taşıma yüksekliği\n• 1 ton/saat'ten 100 ton/saat'e kadar kapasite\n• Paslanmaz veya karbon çelik kova seçenekleri\n• Zincir veya bant tahrik sistemi\n• Toz sızdırmaz gövde tasarımı\n• Kolay bakım için geniş servis kapakları\n• Değişken hız kontrollü motor ve redüktör\n• Emniyet sensörleri (hız, tıkanma, sapma)\n\nUygulama Alanları:\n• Un, irmik, yem ve tahıl işleme tesisleri\n• Kimyasal toz ve granül taşıma\n• Plastik granul, masterbatch ve pelet transferi\n• Madencilik ve cevher taşıma\n• Gıda üretimi (şeker, tuz, baharat, kuruyemiş)\n• Atık geri dönüşüm tesisleri",
    features: ["Kova Tipi Elevatör", "Z Tipi Elevatör", "Toz Sızdırmaz", "Kolay Bakım", "Emniyet Sensörleri", "Değişken Hız"],
    images: [
      "/images/products/elevator/e1.jpg",
      "/images/products/elevator/e2.jpeg",
      "/images/products/elevator/e3.webp"
    ],
    specs: {
      "Kapasite": "1 - 100 Ton/saat",
      "Yükseklik": "5 - 40 metre",
      "Kova Tipi": "Çelik / Plastik",
      "Tahrik": "Zincir / Bant"
    }
  },
  {
    id: "hdpe-projeleri",
    name: "HDPE Projeleri",
    shortDesc: "HDPE tank, boru ve ekipman imalatı",
    fullDesc: "Korozif kimyasalların depolanması ve taşınması için HDPE (Yüksek Yoğunluklu Polietilen) tank, boru ve ekipman imalatı yapıyoruz. Asit, baz ve agresif kimyasallara karşı mükemmel direnç sağlayan HDPE ürünlerimiz, uzun ömürlü ve ekonomik çözümler sunar.",
    features: ["HDPE Tanklar", "HDPE Borular", "Kimyasal Direnci", "Uzun Ömür", "Ekonomik", "Hafif Yapı"],
    images: [
      "/images/products/hdpe/hdpe1.jpeg",
      "/images/products/hdpe/hdpe2.jpeg",
      "/images/products/hdpe/hdpe3.jpeg",
      "/images/products/hdpe/hdpe4.jpeg"
    ],
    specs: {
      "Malzeme": "HDPE PE100",
      "Kapasite": "100L - 100.000L",
      "Sıcaklık": "-40°C / +60°C",
      "Uygulama": "Asit / Baz / Kimyasal"
    }
  },
  {
    id: "basincli-kaplar",
    name: "Basınçlı Kaplar",
    shortDesc: "Yüksek basınç altında çalışan endüstriyel kaplar ve tanklar",
    fullDesc: "Kimya, petrokimya, enerji ve proses endüstrilerinde kullanılan basınçlı kapları tasarlıyor ve üretiyoruz. ASME, PED ve TSE standartlarına uygun olarak imal edilen basınçlı kaplarımız, yüksek güvenlik ve uzun ömürlü kullanım sunar. Reaktörler, buhar kazanları, hava tankları ve özel basınçlı sistemler için anahtar teslim çözümler sağlıyoruz.",
    features: ["ASME Sertifikalı", "PED Uyumlu", "Yüksek Basınç Dayanımı", "Özel Tasarım", "Güvenlik Valfleri", "Test Raporlu"],
    images: [
      "/images/products/basincli-tanklar/FWJN7113.JPG",
      "/images/products/basincli-tanklar/IMG_1033.jpg",
      "/images/products/basincli-tanklar/IMG_1091.JPG",
      "/images/products/basincli-tanklar/FWJN7113.JPG"
    ],
    specs: {
      "Çalışma Basıncı": "1 - 100 Bar",
      "Malzeme": "Karbon Çelik / Paslanmaz / Alaşım",
      "Kapasite": "10L - 100.000L",
      "Sertifikasyon": "ASME / PED / TSE"
    }
  },
  {
    id: "kumlama-boya-izolasyon",
    name: "Kumlama, Boya ve İzolasyon",
    shortDesc: "Sahada kumlama, endüstriyel boya ve profesyonel izolasyon hizmetleri",
    fullDesc: "Mektron Makine olarak, ürettiğimiz paslanmaz ve karbon çelik ekipmanların yanı sıra sahada kumlama, endüstriyel boya ve profesyonel izolasyon hizmetlerini de sunuyoruz.\n\nTesisinizde mevcut tank, reaktör, boru hatları, yapı ve ekipmanlarınızın korozyondan korunması, uzun ömürlü olması ve enerji verimliliğinin artırılması için komple sahada çözüm üretiyoruz.\n\nSunduğumuz Sahada Hizmetler\n\n1. Sahada Kumlama (Sandblasting)\n• SA 2,5 – SA 3 standartlarında endüstriyel kumlama\n• Karbon çelik, paslanmaz çelik ve eski boyalı yüzeyler için\n• Yüksek basınçlı kumlama ekipmanları ile profesyonel uygulama\n• Toz kontrolü ve çevre güvenliği önlemleri\n• Büyük tank, silo, boru ve çelik konstrüksiyonlar için sahada kumlama\n\n2. Endüstriyel Boya ve Korozyon Koruma\n• Epoksi, poliüretan, vinilester ve yüksek sıcaklık boyaları\n• Çift katmanlı ve üç katmanlı boya sistemleri\n• Kimyasal ve asit dayanıklı boyalar\n• Denizcilik ve offshore standartlarında boyama\n• UV ve atmosferik korozyona dayanıklı dış cephe boyaları\n\n3. Isı ve Ses Yalıtım Uygulamaları\n• Boru hatları, tanklar, reaktörler ve ekipman izolasyonu\n• Taş yünü, cam yünü, kauçuk ve PIR/PUR yalıtım malzemeleri\n• Soğuk ve sıcak hat izolasyonu\n• Kondens önleme ve enerji tasarrufu amaçlı izolasyon\n• Ses yalıtımı ve akustik izolasyon uygulamaları\n• Alüminyum, sac veya paslanmaz kaplama ile son kat\n\nNeden Mektron Makine Sahada Hizmetleri?\n• Tek elden hizmet → Kumlama + Boya + İzolasyon komple uygulama\n• Deneyimli Saha Ekibi → Sertifikalı kumlama ve boya ustaları\n• Uluslararası Standartlar → ISO 12944, NACE, SSPC standartlarına uygun uygulama\n• Güvenlik ve Çevre → İş güvenliği önlemleri, toz kontrolü ve atık yönetimi\n• Hızlı ve Kaliteli Uygulama → Minimum üretim duruşu ile profesyonel proje yönetimi\n• Garanti ve Dokümantasyon → Uygulama sonrası garanti belgesi ve raporlar\n\nMevcut tesisinizdeki çelik yapı, tank, boru ve ekipmanlarınızın bakım, yenileme veya koruma projeleri için profesyonel çözüm ortağınızız.",
    features: ["Sahada Kumlama", "Endüstriyel Boya", "Isı Yalıtımı", "Ses Yalıtımı", "Korozyon Koruma", "Tek Elden Hizmet"],
    images: [
      "/images/products/kumlama-boya-izolasyon/b1.webp",
      "/images/products/kumlama-boya-izolasyon/b2.jpg",
      "/images/products/kumlama-boya-izolasyon/b3.jpg",
      "/images/products/kumlama-boya-izolasyon/b4.jpg",
      "/images/products/kumlama-boya-izolasyon/b5.jpg"
    ],
    specs: {
      "Kumlama": "SA 2,5 – SA 3",
      "Boya": "Epoksi / Poliüretan / Vinilester",
      "İzolasyon": "Taş Yünü / Cam Yünü / PIR",
      "Standartlar": "ISO 12944 / NACE / SSPC"
    }
  },
  {
    id: "ozel-imalatlar",
    name: "Özel İmalatlar",
    shortDesc: "Müşteri taleplerine özel tasarım ve imalat çözümleri",
    fullDesc: "Standart ürünlerin dışında, müşterilerimizin özel ihtiyaçlarına yönelik tasarım ve imalat hizmetleri sunuyoruz. Proje bazlı çalışmalarla, sizin belirlediğiniz ölçü, kapasite ve özelliklerde ekipman üretiyoruz. Mühendislik ekibimiz, projenizin her aşamasında yanınızda olarak en uygun çözümü sunar.",
    features: ["Özel Tasarım", "Proje Bazlı Üretim", "Mühendislik Desteği", "3D Modelleme", "Prototip Üretimi", "Anahtar Teslim"],
    images: [
      "/images/products/reaktor-imalati/IMG_4678.jpg",
      "/images/products/esanjor/IMG_5019.jpg",
      "/images/products/mikser/konik-blender.jpg",
      "/images/products/basincli-tanklar/IMG_1033.jpg"
    ],
    specs: {
      "Hizmet": "Tasarım + İmalat + Montaj",
      "Malzeme": "Her Türlü Metal/Plastik",
      "Kapasite": "Projeye Özel",
      "Süre": "Projeye Göre Değişken"
    }
  }
];

// Hizmetler verileri
const services = [
  {
    id: "li-ion-batarya-uretimi",
    name: "Li-ion Batarya Üretimi",
    shortDesc: "Anot ve katot slurry karıştırma tankları tasarım ve üretimi",
    fullDesc: `Mektron Makine olarak, enerji depolama ve batarya üretim sektörüne yönelik çözümlerimiz kapsamında anot ve katot slurry karıştırma tankları tasarlayıp üretmekteyiz. Paslanmaz çelik proses tankları, reaktörler ve mikser sistemleri konusundaki deneyimimizi; pil hücresi üretim hatlarında kullanılan, yüksek viskoziteli anot ve katot çamurlarının (slurry) homojen ve kontrollü şekilde hazırlanmasına uygun özel karıştırma tanklarına taşımış bulunuyoruz.

Batarya üreticilerinin ihtiyaçlarına göre; uygun malzeme seçimi, karıştırma geometrisi, ısıtma/soğutma ceketi, vakum ve emniyet ekipmanları ile birlikte anahtar teslim anot/katot karıştırma tankları sunarak, hem yeni tesis kurulumlarında hem de kapasite artırımı projelerinde güvenilir bir çözüm ortağı olarak hizmet veriyoruz.

Anot Tankında:
• Grafit veya silikonlu grafit (aktif malzeme)
• Carbon black / asetilen siyahı (iletken katkı)
• CMC + SBR (veya PVDF, seçilen kimyaya göre) (bağlayıcı)
• Su veya NMP (çözücü)

Katot Tankında:
• NMC / LFP / NCA / LCO vb. katot tozu (aktif malzeme)
• Carbon black / asetilen siyahı (iletken katkı)
• PVDF (ana bağlayıcı)
• NMP (veya seçilmiş alternatif organik çözücü)

Temel Teknik Özellikler

Malzeme:
• Tüm ürünle temas yüzeyleri: 316L paslanmaz çelik
• İç yüzey pürüzlülüğü: Ra ≤ 0.4 µm (pil, kimya ve ilaç için uygun)

Çalışma Koşulları:
• Basınç: Atmosferik
• Sıcaklık: 10 °C … +80 °C aralığında çalışma

Karıştırma Sistemi (Slurry'ye Uygun):
• Çift şaftlı sistem:
  - Düşük devirli ankastre/anchor karıştırıcı (yüksek viskozite, duvar sıyırma)
  - Yüksek devirli disperser (aktif partikül ve bağlayıcının homojen dağılımı)
• Devir aralıkları (frekans invertörü ile):
  - Anchor: 30–150 rpm
  - Disperser: 500–1500 rpm

Isıtma/Soğutma:
• Dış gövdede yarım bobin (half coil) veya tam ceket
• Isıtma/soğutma suyu veya termal yağ ile
• Hedef: karışım sıcaklığının 25–60 °C bandında, ±2 °C hassasiyetle kontrolü

Temizlik (CIP):
• Üstte 1 veya 2 adet CIP sprey topu
• Otomatik CIP hattına bağlanabilir veya manuel CIP opsiyonu
• Pil kimyası ve solvent kalıntısı kalmayacak şekilde iç yüzey tasarımı

Emniyet ve ATEX:
• NMP gibi solvent kullanılan hatlar için:
  - ATEX sertifikalı motor ve elektrik ekipmanı
  - Uygun topraklama, statik elektrik önlemleri
  - Emniyet ventili, vakum kırıcı ve basınç/vakum göstergeleri`,
    features: ["Çift Şaftlı Karıştırma", "316L Paslanmaz Çelik", "ATEX Sertifikalı", "CIP Temizlik", "Isıtma/Soğutma Ceketi", "Vakum Sistemi"],
    images: [
      "https://images.unsplash.com/photo-1711571603479-40d993b00b95?w=800&q=80",
      "https://images.unsplash.com/photo-1651525670033-279c26cc2347?w=800&q=80",
      "https://images.pexels.com/photos/2760286/pexels-photo-2760286.jpeg?auto=compress&cs=tinysrgb&w=800",
      "https://images.unsplash.com/photo-1742899273038-67ff67477663?w=800&q=80"
    ],
    specs: {
      "Malzeme": "AISI 316L (Ra ≤ 0.4 µm)",
      "Sıcaklık": "10°C – 80°C (±2°C)",
      "Anchor Devir": "30 – 150 RPM",
      "Disperser Devir": "500 – 1500 RPM"
    }
  },
  {
    id: "kapali-kondens-sistemleri",
    name: "Kapalı Kondens Sistemleri",
    shortDesc: "Buhar tesisatlarında maksimum enerji verimliliği ve yakıt tasarrufu",
    fullDesc: `Kapalı Kondens Sistemleri – Buhar Tesisatlarında Maksimum Enerji Verimliliği ve Yakıt Tasarrufu

Açık Sistem Kayıpları:
Atmosfere açık tanklarda kızgın kondens suyu basınçsızlaşarak flaş buhar oluşturur ve bacadan kaybolur. Tank sıcaklığı 85-90°C sınırında kalır, fazlası buharlaşarak çevreye atılır.

Kapalı Kondens Çözümü:
Basınçlı kapalı tank ile yoğuşan kızgın su doğrudan kazana basılır. Sıfır flaş buhar kaybı ile enerjinin %100'e yakını sistemde kalarak yakıt ihtiyacını azaltır.

Sistem Bileşenleri ve Entegrasyon

1. Basınçlı Kondens Tankı
• Kızgın kondens suyunu atmosferle temas ettirmeden toplar
• Bünyesindeki O₂ ve CO₂ korozif gazları mekanik olarak ayrıştırarak korozyonu önler

2. Yüksek Sıcaklık Pompası & Su Yumuşatma
• Kavitasyona dayanıklı özel yüksek sıcaklık pompaları ile suyu direkt buhar kazanına aktarır
• Su yumuşatma ünitesi kireçlenmeyi sıfırlar

Kapalı Sistem Modüler Avantajları

Yakıt Tasarrufu:
Kazana giren besleme suyu sıcaklığı artar, yakıt tüketimi doğrudan minimum %22 oranında azalır.

Su & Kimyasal Tasarrufu:
Taze su ihtiyacı ve su şartlandırması için harcanan tuz ve kimyasal maliyetleri minimuma iner.

Ekipman Koruması:
Eriymiş korozif gazların bertaraf edilmesiyle boru delinmeleri ve kazan korozyonu engellenir.

Sürdürülebilir Yatırım Geri Dönüşü:
Kapalı kondens sistemi yatırımları, işletmelerde sağladığı yakıt, su ve kimyasal tasarrufları sayesinde kendisini ortalama 6 ila 12 ay gibi çok kısa bir sürede amorti eder. Ayrıca blöf ihtiyacını ve çürük buhar atığını ortadan kaldırarak tesisinizin karbon ayak izini belirgin ölçüde düşürür.

Karşılaştırmalı Sistem Analizi

• Su Besleme Sıcaklığı: Açık 80-85°C (Kayıplı) → Kapalı 102-105°C (Kayıpsız)
• Flaş Buhar Atığı: Açık %10-15 Kayıp → Kapalı Sıfır
• Korozyon Riski (O₂/CO₂): Açık Yüksek → Kapalı Minimum
• Yıllık Yakıt Maliyeti: Açık Standart → Kapalı Minimum %22 Düşük

Proje Uygulama Süreci

1. Keşif & Analiz → Mevcut buhar hatlarının ve ısı kayıplarının yerinde ölçümü
2. Projelendirme → Kondens yüküne uygun tank ve pompa kapasite hesabı
3. İmalat & Montaj → Mühendislik standartlarında üretim ve borulama entegrasyonu
4. Devreye Alma → Sistem testleri, otomatik kontrol ve teslimat

Uzun Tesisat Ömrü:
Mektron mühendisliği ile tasarlanan kapalı kondens sistemlerinde, boru hatları ve vanalar korozyondan korunur. Tesisat yıpranması durdurularak beklenmedik duruşların ve arıza bakım masraflarının önüne geçilir.`,
    features: ["%22 Yakıt Tasarrufu", "Sıfır Flaş Buhar Kaybı", "Korozyon Önleme", "Su Tasarrufu", "6-12 Ay Amortisman", "Anahtar Teslim"],
    images: [
      "/images/services/kondens/kondens-tank.jpg",
      "/images/services/kondens/sistem-karsilastirma.png",
      "/images/services/kondens/sistem-bilesenleri.jpg",
      "/images/services/kondens/tesisat-detay.jpg",
      "/images/services/kondens/muhendislik-detay.jpg",
      "/images/services/kondens/proje-sureci.jpg",
      "/images/services/kondens/sistem-analiz.png"
    ],
    specs: {
      "Besleme Sıcaklığı": "102 – 105°C (Kayıpsız)",
      "Yakıt Tasarrufu": "Minimum %22",
      "Flaş Buhar Kaybı": "Sıfır",
      "Amortisman": "6 – 12 Ay"
    }
  }
];


// İletişim Bilgileri
const contactInfo = {
  phone: "+90 212 422 30 13",
  whatsapp: "+90 532 681 57 21",
  email: "info@mektronmakine.com",
  address: "Akçaburgaz Mah. Ahmet Yesevi Cad. Modern İş Merkezi No:35-50 Esenyurt / İstanbul"
};

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { lang, setLang } = useLanguage();
  const t = ui[lang];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-slate-900 shadow-lg' : 'bg-slate-900/80 backdrop-blur-sm'}`}>
      {/* Top Bar */}
      <div className="bg-blue-600 text-white py-2 px-4 text-sm hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-blue-200 transition">
              <Phone size={14} /> {contactInfo.phone}
            </a>
            <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-2 hover:text-blue-200 transition">
              <Mail size={14} /> {contactInfo.email}
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2"><MapPin size={14} /> Esenyurt / İstanbul</span>
            <div className="flex items-center gap-1 ml-4 border-l border-blue-400 pl-4">
              <Globe size={14} />
              {Object.entries(LANGUAGES).map(([code, label]) => (
                <button key={code} onClick={() => setLang(code)} className={`px-2 py-0.5 rounded text-xs font-bold transition ${lang === code ? 'bg-white text-blue-600' : 'hover:bg-blue-500'}`}>{label}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <nav className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Mektron Makine" className="h-12 w-auto" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-white hover:text-blue-400 transition font-medium">{t.nav.home}</Link>
            <Link to="/urunler" className="text-white hover:text-blue-400 transition font-medium">{t.nav.products}</Link>
            <Link to="/hizmetlerimiz" className="text-white hover:text-blue-400 transition font-medium">{t.nav.services}</Link>
            <Link to="/hakkimizda" className="text-white hover:text-blue-400 transition font-medium">{t.nav.about}</Link>
            <Link to="/iletisim" className="text-white hover:text-blue-400 transition font-medium">{t.nav.contact}</Link>
            <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full transition font-medium flex items-center gap-2">
              <Phone size={18} /> {t.nav.callNow}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-white p-2">
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 bg-slate-800 rounded-lg p-4 space-y-4">
            <Link to="/" className="block text-white hover:text-blue-400 transition py-2" onClick={() => setIsMenuOpen(false)}>{t.nav.home}</Link>
            <Link to="/urunler" className="block text-white hover:text-blue-400 transition py-2" onClick={() => setIsMenuOpen(false)}>{t.nav.products}</Link>
            <Link to="/hizmetlerimiz" className="block text-white hover:text-blue-400 transition py-2" onClick={() => setIsMenuOpen(false)}>{t.nav.services}</Link>
            <Link to="/hakkimizda" className="block text-white hover:text-blue-400 transition py-2" onClick={() => setIsMenuOpen(false)}>{t.nav.about}</Link>
            <Link to="/iletisim" className="block text-white hover:text-blue-400 transition py-2" onClick={() => setIsMenuOpen(false)}>{t.nav.contact}</Link>
            <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
              <Globe size={16} className="text-gray-400" />
              {Object.entries(LANGUAGES).map(([code, label]) => (
                <button key={code} onClick={() => setLang(code)} className={`px-3 py-1 rounded text-sm font-bold transition ${lang === code ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>{label}</button>
              ))}
            </div>
            <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="block bg-blue-600 text-white text-center py-3 rounded-lg">
              <Phone size={18} className="inline mr-2" /> {t.nav.callNow}
            </a>
          </div>
        )}
      </nav>
    </header>
  );
};

// WhatsApp Button Component
const WhatsAppButton = () => (
  <a
    href={`https://wa.me/${contactInfo.whatsapp.replace(/\s/g, '').replace(/^\+/, '')}`}
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 z-50 flex items-center gap-2 group"
  >
    <MessageCircle size={28} />
    <span className="hidden group-hover:inline-block whitespace-nowrap pr-2 font-medium">WhatsApp</span>
  </a>
);

// Image Gallery Component
const ImageGallery = ({ images, productName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div>
      {/* Main Gallery */}
      <div className="relative">
        {/* Main Image */}
        <div 
          className="relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer group"
          onClick={() => setIsFullscreen(true)}
        >
          <img 
            src={images[currentIndex]} 
            alt={`${productName} - Görsel ${currentIndex + 1}`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
            <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-4 py-2 rounded-lg">
              Büyütmek için tıklayın
            </span>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button 
          onClick={(e) => { e.stopPropagation(); prevImage(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all hover:scale-110"
        >
          <ChevronLeft size={24} className="text-slate-800" />
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); nextImage(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all hover:scale-110"
        >
          <ChevronRight size={24} className="text-slate-800" />
        </button>

        {/* Image Counter */}
        <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Thumbnails */}
      <div className="grid grid-cols-4 gap-3 mt-4">
        {images.map((img, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`relative aspect-square rounded-lg overflow-hidden transition-all duration-300 ${
              currentIndex === index 
                ? 'ring-4 ring-blue-500 ring-offset-2' 
                : 'opacity-60 hover:opacity-100'
            }`}
          >
            <img 
              src={img} 
              alt={`Thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={() => setIsFullscreen(false)}
        >
          <button 
            onClick={() => setIsFullscreen(false)}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition"
          >
            <X size={32} />
          </button>
          
          <button 
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-4 rounded-full transition"
          >
            <ChevronLeft size={32} className="text-white" />
          </button>
          
          <img 
            src={images[currentIndex]} 
            alt={`${productName} - Görsel ${currentIndex + 1}`}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <button 
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-4 rounded-full transition"
          >
            <ChevronRight size={32} className="text-white" />
          </button>

          {/* Thumbnails in Fullscreen */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((img, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                className={`w-16 h-16 rounded-lg overflow-hidden transition-all ${
                  currentIndex === index ? 'ring-2 ring-white scale-110' : 'opacity-50 hover:opacity-100'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Footer Component
const Footer = () => {
  const { lang } = useLanguage();
  const t = ui[lang];
  return (
  <footer className="bg-slate-900 text-white">
    <div className="max-w-7xl mx-auto px-4 py-16">
      <div className="grid md:grid-cols-4 gap-12">
        <div>
          <div className="mb-6">
            <img src={LOGO_URL} alt="Mektron Makine" className="h-14 w-auto brightness-0 invert" />
          </div>
          <p className="text-gray-400 text-sm leading-relaxed">
            {t.footer.desc}
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-lg mb-6">{t.footer.quickLinks}</h4>
          <ul className="space-y-3">
            <li><Link to="/" className="text-gray-400 hover:text-white transition">{t.nav.home}</Link></li>
            <li><Link to="/urunler" className="text-gray-400 hover:text-white transition">{t.nav.products}</Link></li>
            <li><Link to="/hizmetlerimiz" className="text-gray-400 hover:text-white transition">{t.nav.services}</Link></li>
            <li><Link to="/hakkimizda" className="text-gray-400 hover:text-white transition">{t.nav.about}</Link></li>
            <li><Link to="/iletisim" className="text-gray-400 hover:text-white transition">{t.nav.contact}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-lg mb-6">{t.footer.ourProducts}</h4>
          <ul className="space-y-3">
            {products.slice(0, 6).map(p => (
              <li key={p.id}>
                <Link to={`/urunler/${p.id}`} className="text-gray-400 hover:text-white transition">{getProductText(p, 'name', lang)}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-lg mb-6">{t.footer.contactTitle}</h4>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <MapPin size={20} className="text-blue-500 mt-1 flex-shrink-0" />
              <span className="text-gray-400 text-sm">{contactInfo.address}</span>
            </li>
            <li>
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-3 text-gray-400 hover:text-white transition">
                <Phone size={20} className="text-blue-500" /> {contactInfo.phone}
              </a>
            </li>
            <li>
              <a href={`https://wa.me/${contactInfo.whatsapp.replace(/\s/g, '').replace(/^\+/, '')}`} className="flex items-center gap-3 text-gray-400 hover:text-white transition">
                <MessageCircle size={20} className="text-green-500" /> {contactInfo.whatsapp} (WhatsApp)
              </a>
            </li>
            <li>
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 text-gray-400 hover:text-white transition">
                <Mail size={20} className="text-blue-500" /> {contactInfo.email}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div className="border-t border-slate-800 py-6">
      <div className="max-w-7xl mx-auto px-4 text-center text-gray-500 text-sm">
        © 2024 Mektron Makine. {t.footer.rights}
      </div>
    </div>
  </footer>
);
};

// SEO Component
const SEO = ({ title, description, keywords }) => (
  <Helmet>
    <title>{title}</title>
    <meta name="description" content={description} />
    {keywords && <meta name="keywords" content={keywords} />}
    <meta property="og:title" content={title} />
    <meta property="og:description" content={description} />
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="Mektron Makine" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={title} />
    <meta name="twitter:description" content={description} />
    <link rel="canonical" href="https://mektronmakine.com" />
  </Helmet>
);

// Home Page
const HomePage = () => {
  const { lang } = useLanguage();
  const t = ui[lang];
  const icons = [Shield, Award, Users, Clock, Wrench];
  return (
  <div>
    <SEO 
      title="Mektron Makine | Paslanmaz Çelik Tank, Reaktör ve Endüstriyel Ekipman İmalatı"
      description="Mektron Makine - Kimya, petrokimya, gıda ve ilaç sektörleri için paslanmaz çelik tank, reaktör, eşanjör, mikser, konveyör ve evaporasyon sistemleri imalatı. PED, CE, ASME standartlarında üretim. İstanbul."
      keywords="paslanmaz çelik tank, reaktör imalatı, asit tankı, eşanjör, mikser, konveyör, elevatör, evaporasyon sistemi, gaz yıkama kulesi, scrubber, kükürt ergitme, basınçlı kap, endüstriyel ekipman, İstanbul"
    />
    {/* Hero Section */}
    <section className="relative min-h-screen flex items-center justify-center">
      <div className="absolute inset-0">
        <img src="/images/hero-bg.avif" alt="Endüstriyel Tesis" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/70"></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 pt-32 pb-20">
        <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-sm mb-6">
              <Award size={16} /> {t.hero.badge}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {t.hero.title1} <span className="text-blue-400">{t.hero.title2}</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              {t.hero.desc}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/urunler" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-semibold transition-all hover:scale-105 flex items-center gap-2">
                {t.hero.browseProducts} <ArrowRight size={20} />
              </Link>
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded-full font-semibold transition-all hover:bg-white/10 flex items-center gap-2">
                <Phone size={20} /> {t.nav.callNow}
              </a>
            </div>
        </div>
      </div>
    </section>

    {/* Ne Üretiyoruz Section */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t.whatWeMake.title}</h2>
            <ul className="space-y-3 text-gray-700 text-lg">
              {t.whatWeMake.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3"><ChevronRight className="text-blue-600 mt-1 flex-shrink-0" size={20} /> {item}</li>
              ))}
            </ul>
            <p className="mt-6 text-gray-600 leading-relaxed">{t.whatWeMake.standards}</p>
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t.whatWeMake.fieldTitle}</h2>
            <ul className="space-y-3 text-gray-700 text-lg">
              {t.whatWeMake.fieldItems.map((item, i) => (
                <li key={i} className="flex items-start gap-3"><ChevronRight className="text-blue-600 mt-1 flex-shrink-0" size={20} /> {item}</li>
              ))}
            </ul>
            <div className="mt-8 bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl">
              <p className="text-slate-800 italic font-medium text-lg">"{t.whatWeMake.quote}"</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Neden Mektron Section */}
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t.whyMektron.title}</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
          {t.whyMektron.items.map((item, i) => {
            const Icon = icons[i];
            return (
            <div key={i} className="bg-white p-6 rounded-2xl hover:shadow-xl transition-all hover:-translate-y-2 text-center">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <Icon className="text-blue-600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm">{item.desc}</p>
            </div>
          );})}
        </div>
      </div>
    </section>

    {/* Products Preview */}
    <section className="py-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t.productsSection.title}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">{t.productsSection.desc}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.slice(0, 8).map((product) => (
            <Link 
              key={product.id} 
              to={`/urunler/${product.id}`}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 group"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={product.images[0]} 
                  alt={getProductText(product, 'name', lang)}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition">{getProductText(product, 'name', lang)}</h3>
                <p className="text-gray-600 text-sm line-clamp-2">{getProductText(product, 'shortDesc', lang)}</p>
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/urunler" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-semibold transition">
            {t.productsSection.viewAll} <ArrowRight size={20} />
          </Link>
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t.cta.title}</h2>
        <p className="text-blue-100 text-lg mb-8">{t.cta.desc}</p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition flex items-center gap-2">
            <Phone size={20} /> {contactInfo.phone}
          </a>
          <a 
            href={`https://wa.me/${contactInfo.whatsapp.replace(/\s/g, '').replace(/^\+/, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-semibold transition flex items-center gap-2"
          >
            <MessageCircle size={20} /> WhatsApp
          </a>
        </div>
      </div>
    </section>
  </div>
);
};

// Products List Page
const ProductsPage = () => {
  const { lang } = useLanguage();
  const t = ui[lang];
  return (
  <div className="pt-32 pb-20 bg-gray-50 min-h-screen">
    <SEO
      title="Ürünlerimiz | Mektron Makine - Endüstriyel Ekipman İmalatı"
      description="Mektron Makine ürün kataloğu: Asit tankları, reaktörler, mikserler, eşanjörler, evaporasyon sistemleri, konveyörler, elevatörler ve daha fazlası. Paslanmaz çelik endüstriyel ekipman."
      keywords="paslanmaz çelik ürünler, endüstriyel tank, kimyasal reaktör, proses ekipmanı, stainless steel equipment"
    />
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">{t.productsPage.title}</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">{t.productsPage.desc}</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {products.map((product) => (
          <Link 
            key={product.id} 
            to={`/urunler/${product.id}`}
            className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={product.images[0]} 
                alt={getProductText(product, 'name', lang)}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
            </div>
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition">{getProductText(product, 'name', lang)}</h2>
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{getProductText(product, 'shortDesc', lang)}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {getProductText(product, 'features', lang).slice(0, 3).map((f, i) => (
                  <span key={i} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs">{f}</span>
                ))}
              </div>
              <span className="text-blue-600 font-semibold flex items-center gap-2 text-sm">
                {t.productsSection.viewDetail} <ArrowRight size={16} />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </div>
);
};

// Product Detail Page with Gallery
const ProductDetailPage = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const t = ui[lang];
  const product = products.find(p => p.id === id);

  if (!product) {
    return (
      <div className="pt-32 pb-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">{t.productDetail.notFound}</h1>
          <Link to="/urunler" className="text-blue-600 hover:underline">{t.productDetail.goBack}</Link>
        </div>
      </div>
    );
  }

  const pName = getProductText(product, 'name', lang);

  return (
    <div className="pt-32 pb-20 bg-gray-50 min-h-screen">
      <SEO
        title={`${pName} | Mektron Makine`}
        description={getProductText(product, 'shortDesc', lang) + ' - Mektron Makine endüstriyel ekipman imalatı.'}
        keywords={`${pName}, ${getProductText(product, 'features', lang).join(', ')}, Mektron Makine`}
      />
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-blue-600">{t.breadcrumb.home}</Link>
          <span>/</span>
          <Link to="/urunler" className="hover:text-blue-600">{t.breadcrumb.products}</Link>
          <span>/</span>
          <span className="text-slate-900">{pName}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            <ImageGallery images={product.images} productName={pName} />
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{pName}</h1>
            <div className="text-gray-600 text-lg mb-8 leading-relaxed whitespace-pre-line">{getProductText(product, 'fullDesc', lang)}</div>

            {/* Long Description if exists */}
            {product.longDesc && (
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <div className="text-gray-700 leading-relaxed whitespace-pre-line">{getProductText(product, 'longDesc', lang)}</div>
              </div>
            )}

            {/* Features */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t.productDetail.features}</h3>
              <div className="grid grid-cols-2 gap-3">
                {getProductText(product, 'features', lang).map((feature, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Specs */}
            <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t.productDetail.specs}</h3>
              <div className="space-y-3">
                {Object.entries(product.specs).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                    <span className="text-gray-600">{key}</span>
                    <span className="font-medium text-slate-900">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-wrap gap-4">
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-semibold transition flex items-center gap-2">
                <Phone size={20} /> {t.productDetail.getQuote}
              </a>
              <a 
                href={`https://wa.me/${contactInfo.whatsapp.replace(/\s/g, '').replace(/^\+/, '')}?text=${t.productDetail.whatsappMsg.replace('{name}', pName)}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-semibold transition flex items-center gap-2"
              >
                <MessageCircle size={20} /> {t.productDetail.whatsapp}
              </a>
            </div>
          </div>
        </div>

        {/* Other Products */}
        <div className="mt-20">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">{t.productDetail.otherProducts}</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {products.filter(p => p.id !== id).slice(0, 4).map((p) => (
              <Link 
                key={p.id}
                to={`/urunler/${p.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition group"
              >
                <div className="aspect-video overflow-hidden">
                  <img src={p.images[0]} alt={getProductText(p, 'name', lang)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition">{getProductText(p, 'name', lang)}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// About Page
const AboutPage = () => {
  const { lang } = useLanguage();
  const t = ui[lang];
  return (
  <div className="pt-32 pb-20 bg-gray-50 min-h-screen">
    <SEO
      title="Hakkımızda | Mektron Makine - Endüstriyel Ekipman Üreticisi"
      description="Mektron Makine, 2010'dan bu yana kimya, petrokimya, gıda ve ilaç sektörlerine paslanmaz çelik endüstriyel ekipman tasarım, imalat ve montaj hizmeti vermektedir."
      keywords="Mektron Makine hakkında, endüstriyel ekipman üreticisi, paslanmaz çelik imalat, İstanbul"
    />
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">{t.about.title}</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">{t.about.desc}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
        <div>
          <img 
            src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800&q=80" 
            alt="Fabrikamız"
            className="rounded-2xl shadow-xl"
          />
        </div>
        <div>
          <h2 className="text-3xl font-bold text-slate-900 mb-6">{t.about.companyName}</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">{t.about.p1}</p>
          <p className="text-gray-600 mb-6 leading-relaxed">{t.about.p2}</p>
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center p-4 bg-white rounded-xl shadow">
              <div className="text-3xl font-bold text-blue-600 mb-1">500+</div>
              <div className="text-gray-600 text-sm">{t.about.stat1}</div>
            </div>
            <div className="text-center p-4 bg-white rounded-xl shadow">
              <div className="text-3xl font-bold text-blue-600 mb-1">15</div>
              <div className="text-gray-600 text-sm">{t.about.stat2}</div>
            </div>
            <div className="text-center p-4 bg-white rounded-xl shadow">
              <div className="text-3xl font-bold text-blue-600 mb-1">100+</div>
              <div className="text-gray-600 text-sm">{t.about.stat3}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const { lang } = useLanguage();
  const t = ui[lang];

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="pt-32 pb-20 bg-gray-50 min-h-screen">
      <SEO
        title="İletişim | Mektron Makine - Teklif Alın"
        description="Mektron Makine ile iletişime geçin. Paslanmaz çelik tank, reaktör ve endüstriyel ekipman projeleri için teklif alın. Tel: +90 212 422 30 13, Esenyurt/İstanbul."
        keywords="Mektron Makine iletişim, endüstriyel ekipman teklif, paslanmaz çelik tank fiyat, İstanbul"
      />
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">{t.contact.title}</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">{t.contact.desc}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">{t.contact.infoTitle}</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{t.contact.address}</h3>
                    <p className="text-gray-600">{contactInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{t.contact.phone}</h3>
                    <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-blue-600 hover:underline">{contactInfo.phone}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">WhatsApp</h3>
                    <a 
                      href={`https://wa.me/${contactInfo.whatsapp.replace(/\s/g, '').replace(/^\+/, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:underline"
                    >
                      {contactInfo.whatsapp}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{t.contact.email}</h3>
                    <a href={`mailto:${contactInfo.email}`} className="text-blue-600 hover:underline">{contactInfo.email}</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3008.5!2d28.6!3d41.05!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDAzJzAwLjAiTiAyOMKwMzYnMDAuMCJF!5e0!3m2!1str!2str!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mektron Makine Konum"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">{t.contact.formTitle}</h2>
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{t.contact.success}</h3>
                <p className="text-gray-600">{t.contact.successDesc}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.name}</label>
                    <input type="text" required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.phoneLbl}</label>
                    <input type="tel" className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.emailLbl}</label>
                  <input type="email" required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.subject}</label>
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})}>
                    <option value="">{t.contact.subjects.select}</option>
                    <option value="teklif">{t.contact.subjects.quote}</option>
                    <option value="bilgi">{t.contact.subjects.info}</option>
                    <option value="destek">{t.contact.subjects.support}</option>
                    <option value="diger">{t.contact.subjects.other}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.message}</label>
                  <textarea rows={5} required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})}></textarea>
                </div>
                <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold transition">{t.contact.send}</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Services List Page
const ServicesPage = () => {
  const { lang } = useLanguage();
  const t = ui[lang];
  return (
  <div className="pt-32 pb-20 bg-gray-50 min-h-screen">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">{t.nav.services}</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">{lang === 'tr' ? 'Enerji verimliliği ve endüstriyel çözümlerimiz' : lang === 'en' ? 'Our energy efficiency and industrial solutions' : 'Наши решения в области энергоэффективности'}</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {services.map((service) => (
          <Link key={service.id} to={`/hizmetlerimiz/${service.id}`} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={service.images[0]} alt={service.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition">{service.name}</h2>
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{service.shortDesc}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {service.features.slice(0, 3).map((f, i) => (
                  <span key={i} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs">{f}</span>
                ))}
              </div>
              <span className="text-blue-600 font-semibold flex items-center gap-2 text-sm">{t.productsSection.viewDetail} <ArrowRight size={16} /></span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </div>
);
};

// Service Detail Page
const ServiceDetailPage = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const t = ui[lang];
  const service = services.find(s => s.id === id);
  if (!service) return (<div className="pt-32 pb-20 min-h-screen flex items-center justify-center"><div className="text-center"><h1 className="text-2xl font-bold text-slate-900 mb-4">{t.productDetail.notFound}</h1><Link to="/hizmetlerimiz" className="text-blue-600 hover:underline">{t.nav.services}</Link></div></div>);

  // Kondens sayfası özel layout
  if (service.id === "kapali-kondens-sistemleri") {
    return (
      <div className="pt-32 pb-20 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <Link to="/" className="hover:text-blue-600">{t.breadcrumb.home}</Link><span>/</span>
            <Link to="/hizmetlerimiz" className="hover:text-blue-600">{t.nav.services}</Link><span>/</span>
            <span className="text-slate-900">{service.name}</span>
          </div>

          {/* Hero */}
          <div className="bg-gradient-to-r from-blue-700 to-blue-900 rounded-2xl p-8 md:p-12 mb-12 text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">Kapalı Kondens Sistemleri</h1>
            <p className="text-xl text-blue-100">Buhar Tesisatlarında Maksimum Enerji Verimliliği ve Yakıt Tasarrufu</p>
          </div>

          {/* Slide 1: Açık vs Kapalı */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-8">
              <h3 className="text-xl font-bold text-red-700 mb-4">Açık Sistem Kayıpları</h3>
              <p className="text-gray-700 mb-3">Atmosfere açık tanklarda kızgın kondens suyu basınçsızlaşarak flaş buhar oluşturur ve bacadan kaybolur.</p>
              <p className="text-gray-700"><strong>Yüksek Isı Kaybı:</strong> Tank sıcaklığı 85-90°C sınırında kalır, fazlası buharlaşarak çevreye atılır.</p>
            </div>
            <div className="bg-green-50 border-l-4 border-green-500 rounded-xl p-8">
              <h3 className="text-xl font-bold text-green-700 mb-4">Kapalı Kondens Çözümü</h3>
              <p className="text-gray-700 mb-3">Basınçlı kapalı tank ile yoğuşan kızgın su doğrudan kazana basılır.</p>
              <p className="text-gray-700"><strong>Sıfır Flaş Buhar Kaybı:</strong> Enerjinin %100'e yakını sistemde kalarak yakıt ihtiyacını azaltır.</p>
            </div>
          </div>

          {/* Slide 2: Sistem Bileşenleri + Resim */}
          <div className="grid md:grid-cols-2 gap-8 mb-12 items-center">
            <div>
              <img src="/images/services/kondens/sistem-bilesenleri.jpg" alt="Buhar Tesisatı" className="rounded-2xl shadow-lg w-full" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Sistem Bileşenleri ve Entegrasyon</h2>
              <div className="space-y-6">
                <div className="bg-white p-5 rounded-xl shadow-sm">
                  <h4 className="font-bold text-blue-600 mb-2">1. Basınçlı Kondens Tankı</h4>
                  <p className="text-gray-600">Kızgın kondens suyunu atmosferle temas ettirmeden toplar. Bünyesindeki O₂ ve CO₂ korozif gazları mekanik olarak ayrıştırarak korozyonu önler.</p>
                </div>
                <div className="bg-white p-5 rounded-xl shadow-sm">
                  <h4 className="font-bold text-blue-600 mb-2">2. Yüksek Sıcaklık Pompası & Su Yumuşatma</h4>
                  <p className="text-gray-600">Kavitasyona dayanıklı özel yüksek sıcaklık pompaları ile suyu direkt buhar kazanına aktarır. Su yumuşatma ünitesi kireçlenmeyi sıfırlar.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Slide 3: Modüler Avantajlar */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Kapalı Sistem Modüler Avantajları</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-lg text-center hover:-translate-y-2 transition-all">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">%22</span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Yakıt Tasarrufu</h3>
                <p className="text-gray-600 text-sm">Kazana giren besleme suyu sıcaklığı artar, yakıt tüketimi doğrudan minimum %22 oranında azalır.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-lg text-center hover:-translate-y-2 transition-all">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="text-green-600" size={28} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Su & Kimyasal Tasarrufu</h3>
                <p className="text-gray-600 text-sm">Taze su ihtiyacı ve su şartlandırması için harcanan tuz ve kimyasal maliyetleri minimuma iner.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-lg text-center hover:-translate-y-2 transition-all">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="text-orange-600" size={28} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Ekipman Koruması</h3>
                <p className="text-gray-600 text-sm">Korozif gazların bertaraf edilmesiyle boru delinmeleri ve kazan korozyonu engellenir.</p>
              </div>
            </div>
          </div>

          {/* Slide 4: Yatırım Geri Dönüşü + Resim */}
          <div className="grid md:grid-cols-2 gap-8 mb-12 items-center">
            <div className="bg-blue-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">%22 Net Enerji Tasarrufu</h2>
              <h3 className="text-lg font-semibold text-blue-600 mb-4">Sürdürülebilir Yatırım Geri Dönüşü</h3>
              <p className="text-gray-700 leading-relaxed">Kapalı kondens sistemi yatırımları, işletmelerde sağladığı yakıt, su ve kimyasal tasarrufları sayesinde kendisini ortalama <strong>6 ila 12 ay</strong> gibi çok kısa bir sürede amorti eder.</p>
              <p className="text-gray-700 mt-3 leading-relaxed">Ayrıca blöf ihtiyacını ve çürük buhar atığını ortadan kaldırarak tesisinizin karbon ayak izini belirgin ölçüde düşürür.</p>
            </div>
            <div>
              <img src="/images/services/kondens/tesisat-detay.jpg" alt="Tesisat Detayı" className="rounded-2xl shadow-lg w-full" />
            </div>
          </div>

          {/* Slide 5: Karşılaştırmalı Tablo */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Karşılaştırmalı Sistem Analizi</h2>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-800 text-white">
                  <tr>
                    <th className="text-left p-4 font-semibold">Parametre</th>
                    <th className="text-left p-4 font-semibold">Klasik Açık Sistem</th>
                    <th className="text-left p-4 font-semibold text-blue-300">Mektron Kapalı Sistem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr><td className="p-4 text-gray-700">Su Besleme Sıcaklığı</td><td className="p-4 text-red-600">80-85°C (Kayıplı)</td><td className="p-4 text-green-600 font-semibold">102-105°C (Kayıpsız)</td></tr>
                  <tr className="bg-gray-50"><td className="p-4 text-gray-700">Flaş Buhar Atığı</td><td className="p-4 text-red-600">Yüksek (%10-15 Kayıp)</td><td className="p-4 text-green-600 font-semibold">Sıfır (Sistem İçinde Korunur)</td></tr>
                  <tr><td className="p-4 text-gray-700">Korozyon Riski (O₂/CO₂)</td><td className="p-4 text-red-600">Yüksek (Boru delinmeleri sık)</td><td className="p-4 text-green-600 font-semibold">Minimum</td></tr>
                  <tr className="bg-gray-50"><td className="p-4 text-gray-700">Yıllık Yakıt Maliyeti</td><td className="p-4 text-red-600">Standart Yüksek Tüketim</td><td className="p-4 text-green-600 font-semibold">Minimum %22 Düşük</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Slide 6: Mühendislik + 3D Render */}
          <div className="grid md:grid-cols-2 gap-8 mb-12 items-center">
            <div>
              <img src="/images/services/kondens/sistem-analiz.png" alt="3D Sistem Görseli" className="rounded-2xl shadow-lg w-full" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Mühendislik Detayları</h2>
              <h3 className="text-lg font-semibold text-blue-600 mb-4">Uzun Tesisat Ömrü</h3>
              <p className="text-gray-700 leading-relaxed mb-4">Mektron mühendisliği ile tasarlanan kapalı kondens sistemlerinde, boru hatları ve vanalar korozyondan korunur. Tesisat yıpranması durdurularak beklenmedik duruşların ve arıza bakım masraflarının önüne geçilir.</p>
            </div>
          </div>

          {/* Slide 7: Proje Süreci */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Proje Uygulama Süreci</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: "1", title: "Keşif & Analiz", desc: "Mevcut buhar hatlarının ve ısı kayıplarının yerinde ölçümü." },
                { step: "2", title: "Projelendirme", desc: "Kondens yüküne uygun tank ve pompa kapasite hesabı." },
                { step: "3", title: "İmalat & Montaj", desc: "Mühendislik standartlarında üretim ve borulama entegrasyonu." },
                { step: "4", title: "Devreye Alma", desc: "Sistem testleri, otomatik kontrol ve teslimat." }
              ].map((item) => (
                <div key={item.step} className="bg-white rounded-2xl p-6 shadow-lg text-center relative">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">{item.step}</div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Proje Görseli */}
          <div className="mb-12">
            <img src="/images/services/kondens/proje-sureci.jpg" alt="Proje Uygulama" className="rounded-2xl shadow-lg w-full" />
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Tesisiniz İçin Teklif Alın</h2>
            <p className="text-blue-100 mb-6">Buhar tesisatınızda enerji verimliliğini artırmak için bizimle iletişime geçin.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition flex items-center gap-2"><Phone size={20} /> {contactInfo.phone}</a>
              <a href={`https://wa.me/${contactInfo.whatsapp.replace(/\s/g, '').replace(/^\+/, '')}?text=${encodeURIComponent('Merhaba, Kapalı Kondens Sistemleri hakkında bilgi almak istiyorum.')}`} target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-semibold transition flex items-center gap-2"><MessageCircle size={20} /> WhatsApp</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Diğer hizmetler için standart layout
  return (
    <div className="pt-32 pb-20 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-blue-600">{t.breadcrumb.home}</Link><span>/</span>
          <Link to="/hizmetlerimiz" className="hover:text-blue-600">{t.nav.services}</Link><span>/</span>
          <span className="text-slate-900">{service.name}</span>
        </div>
        <div className="grid lg:grid-cols-2 gap-12">
          <div><ImageGallery images={service.images} productName={service.name} /></div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{service.name}</h1>
            <div className="text-gray-600 text-lg mb-8 leading-relaxed whitespace-pre-line">{service.fullDesc}</div>
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t.productDetail.features}</h3>
              <div className="grid grid-cols-2 gap-3">
                {service.features.map((f, i) => (<div key={i} className="flex items-center gap-2"><div className="w-2 h-2 bg-blue-500 rounded-full"></div><span className="text-gray-700">{f}</span></div>))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t.productDetail.specs}</h3>
              <div className="space-y-3">
                {Object.entries(service.specs).map(([key, value]) => (<div key={key} className="flex justify-between py-2 border-b border-gray-100 last:border-0"><span className="text-gray-600">{key}</span><span className="font-medium text-slate-900">{value}</span></div>))}
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-semibold transition flex items-center gap-2"><Phone size={20} /> {t.productDetail.getQuote}</a>
              <a href={`https://wa.me/${contactInfo.whatsapp.replace(/\s/g, '').replace(/^\+/, '')}?text=${t.productDetail.whatsappMsg.replace('{name}', service.name)}`} target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-semibold transition flex items-center gap-2"><MessageCircle size={20} /> {t.productDetail.whatsapp}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App
function App() {
  return (
    <LanguageProvider>
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/urunler" element={<ProductsPage />} />
          <Route path="/urunler/:id" element={<ProductDetailPage />} />
          <Route path="/hizmetlerimiz" element={<ServicesPage />} />
          <Route path="/hizmetlerimiz/:id" element={<ServiceDetailPage />} />
          <Route path="/hakkimizda" element={<AboutPage />} />
          <Route path="/iletisim" element={<ContactPage />} />
        </Routes>
        <Footer />
        <WhatsAppButton />
      </BrowserRouter>
    </div>
    </LanguageProvider>
  );
}

export default App;
